import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import { Button } from '@src/design-system/button';
import { Bell } from 'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@src/design-system/tabs';
import { Link } from 'react-router-dom';
import { NotificationSection } from './notification-section';
import { NotificationItem } from './notification-item';
import { createRoute, PATHS } from '@src/core/router/paths';
import { Text } from '@src/design-system/text';
import { useRef, useState } from 'react';
import notificationApi from '../services/notification-api';
import { useDateSplitter } from '../hooks/use-date-spliter';
import { useNotificationsInfinityScroll } from '../hooks/use-infinity-scroll';
import { NotificationReadAll } from './notification-read-all';

export const NotificationMenu = () => {
  const [isOpen, setIsOpen] = useState(false);

  const { data: { count } = { count: 0 }, isLoading: isCountLoading } =
    notificationApi.useUnreadCount();

  const { notifications, isNotificationsEmpty, containerRef } =
    useNotificationsInfinityScroll({ queryParam: {}, isOpen });

  const { Today, Yesterday, Last7Days, Older } = useDateSplitter(
    notifications || []
  );

  const sections = [
    { title: 'Today', notifications: Today },
    { title: 'Yesterday', notifications: Yesterday },
    { title: 'Last 7 Days', notifications: Last7Days },
    { title: 'Older', notifications: Older },
  ];

  const btnRef = useRef<HTMLButtonElement>(null);
  const onOpenChange = (status: boolean) => {
    setIsOpen(status);
    if (!btnRef.current) return;
    btnRef.current.classList.toggle('active');
  };
  return (
    <Popover onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          ref={btnRef}
          variant={'primaryOutline'}
          size={'iconLg'}
          className='[&.active]:!bg-primary-900  [&.active]:!text-white relative'
        >
          {!isCountLoading && count > 0 && (
            <span className='absolute flex items-center justify-center top-[-4px] aspect-square size-4 right-[-4px] bg-red-500 p-1 rounded-full text-white text-[8px]'>
              {count}
            </span>
          )}
          <Bell />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-full sm:!w-[520px] max-w-full sm:max-w-[520px] px-0 py-2 bg-[#FAF9FC] mx-auto'>
        <div className='p-4 pb-2 flex items-center justify-between mb-4'>
          <Text variant='h4'>Notifications</Text>
        </div>

        <Tabs defaultValue='notifications'>
          <TabsList className='grid grid-cols-2 w-full border-b flex bg-white px-4 py-8'>
            <TabsTrigger
              value='notifications'
              className='relative font-semibold border-b-2 border-transparent data-[state=active]:border-[#5B31AA]'
            >
              Notifications {!isCountLoading && `(${count})`}
            </TabsTrigger>
            <TabsTrigger
              value='requests'
              className='relative font-semibold border-b-2 border-transparent data-[state=active]:border-[#5B31AA]'
            >
              Requests
            </TabsTrigger>
            <NotificationReadAll />
          </TabsList>

          <TabsContent value='notifications'>
            {isNotificationsEmpty ? (
              <div className='p-6 text-center text-gray-500 text-sm'>
                No notifications found.
              </div>
            ) : (
              <div
                className='max-h-[61vh] 3xl:max-h-[600px] overflow-y-auto scrollbar-gray mb-2'
                ref={containerRef}
              >
                <div className='space-y-6 p-4'>
                  {sections.map(
                    ({ title, notifications }) =>
                      notifications.length > 0 && (
                        <NotificationSection
                          key={title}
                          title={title}
                        >
                          {notifications.map((item) => (
                            <NotificationItem
                              key={item.id}
                              data={item}
                            />
                          ))}
                        </NotificationSection>
                      )
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value='requests'>
            <div className='p-6 text-center text-gray-500 text-sm'>
              No new requests.
            </div>
          </TabsContent>
        </Tabs>
        <div className='px-3 max-h-[45px] text-center flex justify-end'>
          <Link
            to={createRoute(PATHS.Notifications)}
            className='bg-primary-100 border border-primary-900 py-2 px-4 hover:bg-border text-primary-900 bg-transparent rounded-xl min-w-full sm:min-w-[230px]'
          >
            Go to Notification Center
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
};
